import { supportService } from '@/services/support'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { supportKeys } from './query-keys'
import type { SupportAnalyticsData, SupportMessage } from './types'
import { supportUtils } from './utils'

// Basic queries
export const useSupportMessage = (
  messageId: string,
  options?: Omit<UseQueryOptions<SupportMessage | null, Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: supportKeys.detail(messageId),
    queryFn: () => supportService.getSupportMessageById(messageId),
    enabled: !!messageId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

export const useAllSupportMessages = (
  options?: Omit<UseQueryOptions<SupportMessage[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: supportKeys.list({}),
    queryFn: () => supportService.getAllSupportMessages(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  })
}

export const useUserSupportMessages = (
  userId: string,
  options?: Omit<UseQueryOptions<SupportMessage[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: supportKeys.userMessages(userId),
    queryFn: () => supportService.getSupportMessagesByUser(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  })
}

// Optimized queries with transformations
export const useSupportMessagesByStatus = (
  status: string,
  options?: Omit<UseQueryOptions<SupportMessage[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: supportKeys.list({ status }),
    queryFn: () => supportService.getAllSupportMessages(),
    select: (data: SupportMessage[]) => supportUtils.filterByStatus(data, status),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  })
}

export const useSupportAnalytics = (
  options?: Omit<UseQueryOptions<SupportMessage[], Error, SupportAnalyticsData[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  return useQuery({
    queryKey: supportKeys.list({ analytics: true }),
    queryFn: () => supportService.getAllSupportMessages(),
    select: supportUtils.toAnalyticsData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

export const useSupportStatusCounts = (
  options?: Omit<
    UseQueryOptions<SupportMessage[], Error, ReturnType<typeof supportUtils.getStatusCounts>>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  return useQuery({
    queryKey: supportKeys.list({ statusCounts: true }),
    queryFn: () => supportService.getAllSupportMessages(),
    select: supportUtils.getStatusCounts,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

export const useFilteredSupportMessages = (
  searchTerm: string,
  options?: Omit<UseQueryOptions<SupportMessage[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: supportKeys.list({ search: searchTerm }),
    queryFn: () => supportService.getAllSupportMessages(),
    select: (data: SupportMessage[]) => supportUtils.searchMessages(data, searchTerm),
    enabled: searchTerm.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  })
}
